import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Tv, ShieldCheck, Zap, Rocket, Star, Info, Monitor, Smartphone, LayoutGrid, ListChecks } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/tutoriais-iptv/lg")({
  head: () => ({
    meta: [
      { title: "Como Instalar IPTV na LG Smart TV: Guia Completo Passo a Passo" },
      { name: "description", content: "Aprenda como configurar IPTV na sua LG Smart TV. Guia completo de instalação de aplicativos, melhores players e como revender com Painel IPTV Pós Pago." },
    ],
  }),
  component: LGTutorial,
});

function LGTutorial() {
  const apps = [
    { name: "One X", href: "/aplicativos-iptv/one-x", desc: "App exclusivo com estabilidade premium." },
    { name: "P2PRO", href: "/aplicativos-iptv/p2pro", desc: "Desempenho e praticidade absoluta." },
    { name: "P2PLAY", href: "/aplicativos-iptv/p2play", desc: "Rápido, intuitivo e leve para sua LG." },
    { name: "Prime Play", href: "/aplicativos-iptv/prime-play", desc: "Vasto catálogo e alta compatibilidade." },
    { name: "Blessed", href: "/aplicativos-iptv/blessed", desc: "Interface amigável e fácil configuração." },
    { name: "Fun Play", href: "/aplicativos-iptv/funplay", desc: "Excelente desempenho em TVs WebOS." },
    { name: "Lazer Play", href: "/aplicativos-iptv/lazer-play", desc: "Design moderno e navegação fluida." },
    { name: "Assist Plus", href: "/aplicativos-iptv/assist-plus", desc: "Instalação simples e ótima experiência." },
    { name: "PlaySim", href: "/aplicativos-iptv/playsim", desc: "Otimizado para LG e Roku TV." },
    { name: "VuPlayer", href: "/aplicativos-iptv/vuplayer", desc: "Versátil para múltiplas plataformas." },
    { name: "IBO Player", href: "/aplicativos-iptv/ibo-player", desc: "O player mais popular do mercado." }
  ];

  const devices = [
    { name: "LG Smart TV", icon: <Tv className="w-5 h-5" /> },
    { name: "Samsung TV", icon: <Monitor className="w-5 h-5" /> },
    { name: "Roku TV", icon: <Tv className="w-5 h-5" /> },
    { name: "Android", icon: <Smartphone className="w-5 h-5" /> },
    { name: "iPhone", icon: <Smartphone className="w-5 h-5" /> }
  ];

  const comparisonRows = [
    { name: "One X", ui: "Premium", speed: "Ultra", resolution: "Sim" },
    { name: "P2PRO", ui: "Moderna", speed: "Rápida", resolution: "Sim" },
    { name: "P2PLAY", ui: "Simples", speed: "Rápida", resolution: "Sim" },
    { name: "Prime Play", ui: "Completa", speed: "Média", resolution: "Sim" }
  ];

  return (
    <SEOPage
      title="Como Instalar IPTV na LG Smart TV: Guia Passo a Passo"
      subtitle="Transforme sua LG Smart TV em uma central de entretenimento completa com os melhores aplicativos IPTV do mercado."
      category="Tutoriais LG"
      introParagraphs={[
        "As Smart TVs LG, equipadas com o sistema WebOS, são reconhecidas mundialmente pela fluidez e excelente qualidade de imagem. Para usuários de IPTV, elas oferecem uma das melhores experiências do mercado devido à compatibilidade com players de alta performance.",
        "Neste guia completo, você aprenderá como configurar sua estrutura IPTV profissional utilizando o sistema de Painel IPTV Pós Pago, garantindo estabilidade e o melhor custo-benefício para sua revenda."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "LG Smart TV", href: "/tutoriais-iptv/lg" }
      ]}
      benefits={[
        { title: "Qualidade 4K/HDR", description: "Otimização total para a tecnologia de imagem das TVs LG." },
        { title: "WebOS Native", description: "Apps desenvolvidos especificamente para o sistema da LG." },
        { title: "Sem Travamentos", description: "Estabilidade garantida com nossos servidores premium." },
        { title: "Setup em 2 Minutos", description: "Configuração rápida e intuitiva para qualquer usuário." },
        { title: "Controle Remoto", description: "Compatibilidade total com o Magic Remote da LG." },
        { title: "Painel Pós-Pago", description: "Gestão profissional para revendedores de sucesso." }
      ]}
      howItWorks={[
        { title: "Conexão", description: "Certifique-se que sua LG TV está conectada à internet." },
        { title: "Content Store", description: "Abra a loja oficial de aplicativos da sua TV." },
        { title: "Busca", description: "Pesquise por um de nossos apps recomendados (ex: One X)." },
        { title: "Instalação", description: "Instale, abra o app e insira seus dados de acesso." }
      ]}
      diferenciais={[
        { title: "Zero Estoque", description: "Não compre créditos adiantados. Pague após o uso." },
        { title: "Marca Própria", description: "Possibilidade de ter sua própria marca no painel." },
        { title: "Ativação 24h", description: "Sistema automatizado para liberação de clientes." },
        { title: "Suporte VIP", description: "Auxílio técnico especializado para sua operação." }
      ]}
      faq={[
        { question: "Posso instalar IPTV em qualquer TV LG?", answer: "A maioria das Smart TVs LG com sistema WebOS (fabricadas após 2014) suportam nossos aplicativos oficiais." },
        { question: "Preciso de TV Box para usar IPTV na LG?", answer: "Não é necessário. Nossos aplicativos rodam nativamente no sistema da TV, economizando hardware extra." },
        { question: "Qual o melhor aplicativo para LG?", answer: "Recomendamos o One X, P2PRO e Prime Play pela estabilidade e recursos avançados de interface." },
        { question: "O Painel IPTV Pós Pago é seguro?", answer: "Sim, é o modelo mais seguro do mercado atual, permitindo que você gerencie seu negócio sem riscos financeiros." },
        { question: "Como recebo o login de acesso?", answer: "Ao se tornar um revendedor ou cliente, você recebe as credenciais instantaneamente via WhatsApp ou Painel." }
      ]}
      sections={[
        {
          title: "Compatibilidade Multi-Dispositivo",
          content: (
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {devices.map((device, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-bold uppercase tracking-widest">
                  {device.icon}
                  {device.name}
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Os Melhores Aplicativos para LG",
          type: "grid",
          content: (
            <>
              {apps.map((app, i) => (
                <Link 
                  key={i}
                  to={app.href as any}
                  className="group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-electric/5 hover:border-electric/30 transition-all flex flex-col h-full"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold group-hover:text-electric transition-colors">{app.name}</h3>
                    <Rocket className="w-4 h-4 text-electric opacity-0 group-hover:opacity-100 transition-all translate-x-[-10px] group-hover:translate-x-0" />
                  </div>
                  <p className="text-muted-foreground text-xs leading-relaxed mb-4 flex-grow">{app.desc}</p>
                  <span className="text-[10px] font-black uppercase tracking-widest text-electric/60 group-hover:text-electric flex items-center gap-2">
                    Ver Tutorial <Zap className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </>
          )
        },
        {
          title: "Comparativo de Players",
          content: (
            <div className="overflow-x-auto rounded-2xl border border-white/5 bg-white/[0.01]">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="p-4 font-bold uppercase tracking-widest text-[10px]">Aplicativo</th>
                    <th className="p-4 font-bold uppercase tracking-widest text-[10px]">Interface</th>
                    <th className="p-4 font-bold uppercase tracking-widest text-[10px]">Velocidade</th>
                    <th className="p-4 font-bold uppercase tracking-widest text-[10px]">Suporte 4K</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {comparisonRows.map((row, i) => (
                    <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                      <td className="p-4 font-bold text-electric">{row.name}</td>
                      <td className="p-4 text-muted-foreground">{row.ui}</td>
                      <td className="p-4 text-muted-foreground">{row.speed}</td>
                      <td className="p-4 text-muted-foreground">{row.resolution}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        },
        {
          title: "Por que escolher o Painel Pós Pago?",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-electric/20 flex items-center justify-center text-electric">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h4 className="font-bold">Risco Zero</h4>
                <p className="text-muted-foreground text-sm">Elimine a necessidade de estoque de créditos. Você só paga pelo que seus clientes consomem ativamente.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-electric/20 flex items-center justify-center text-electric">
                  <LayoutGrid className="w-6 h-6" />
                </div>
                <h4 className="font-bold">Apps Exclusivos</h4>
                <p className="text-muted-foreground text-sm">Acesso a players premium como One X, desenvolvidos para máxima performance na LG.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-electric/20 flex items-center justify-center text-electric">
                  <ListChecks className="w-6 h-6" />
                </div>
                <h4 className="font-bold">Controle Total</h4>
                <p className="text-muted-foreground text-sm">Gerenciamento completo de renovações, testes e suporte em uma interface única e intuitiva.</p>
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Quer Ter Seu Próprio Painel IPTV Pós Pago?"
      ctaDescription="Aproveite a melhor infraestrutura do mercado para Smart TVs LG e comece a lucrar hoje mesmo com ativação imediata."
    />
  );
}
